import { useRef } from 'react';
import { useFrame, extend } from '@react-three/fiber';
import * as THREE from 'three';
import { WaterShaderMaterial } from '../shaders/waterShader';
import { EYE_SPHERE, FLOOR_WORLD_Y } from '../../constants/scene';

extend({ WaterShaderMaterial });

const _mouseWorld = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _targetQuat = new THREE.Quaternion();
const _lookTarget = new THREE.Vector3();

// Shared mouse world position so the reflection can read it
export const sharedMouseWorld = new THREE.Vector3(0, 0, 5);
const _mirroredLight = new THREE.Vector3();

export default function EyeSphere({ reflection = false }) {
  const sphereRef = useRef();
  const materialRef = useRef();

  useFrame((state, delta) => {
    if (!reflection && materialRef.current) {
      materialRef.current.uTime = state.clock.elapsedTime;

      // Project mouse pointer into world space on a plane in front of the sphere
      const lightPlaneZ = EYE_SPHERE.radius + 0.5;
      _mouseWorld.set(state.pointer.x, state.pointer.y, 0.5);
      _mouseWorld.unproject(state.camera);
      _dir.copy(_mouseWorld).sub(state.camera.position).normalize();
      const dist = (lightPlaneZ - state.camera.position.z) / _dir.z;
      _mouseWorld.copy(state.camera.position).addScaledVector(_dir, dist);

      materialRef.current.uMouseLight.copy(_mouseWorld);
      sharedMouseWorld.copy(_mouseWorld);
    }

    // Reflection sphere: sync time and mirror the mouse light across the floor plane
    if (reflection && materialRef.current) {
      materialRef.current.uTime = state.clock.elapsedTime;
      _mirroredLight.copy(sharedMouseWorld);
      _mirroredLight.y = 2 * FLOOR_WORLD_Y - _mirroredLight.y;
      materialRef.current.uMouseLight.copy(_mirroredLight);
    }

    if (!reflection && sphereRef.current) {
      // Tilt sphere toward cursor (like a flashlight illuminating a tiny world)
      _lookTarget.set(_mouseWorld.x * 0.3, _mouseWorld.y * 0.3, 5.0);
      const mesh = sphereRef.current;
      mesh.lookAt(_lookTarget);
      _targetQuat.copy(mesh.quaternion);

      // Smoothly interpolate toward target orientation
      mesh.quaternion.slerp(_targetQuat, 1.0 - Math.exp(-3.0 * delta));
    }
  });

  return (
    <mesh ref={sphereRef} position={EYE_SPHERE.position}>
      <sphereGeometry
        args={[EYE_SPHERE.radius, EYE_SPHERE.segments, EYE_SPHERE.segments]}
      />
      <waterShaderMaterial
        ref={materialRef}
        transparent={reflection}
        uAlpha={reflection ? EYE_SPHERE.reflectionAlpha : 1.0}
      />
    </mesh>
  );
}
